import React, { useEffect, useState, useCallback } from "react";
import Iframe from "../../../components/Iframe";
import Breadcrumbs from "../../../components/Breadcrumbs";
import {
  handlePriceSectionAdd,
  handlePriceItemAdd,
  MESSAGE_TYPES,
} from "../../../utils/editUtils";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import {
  executeValidationWithArrays,
  handleResetWithValidation,
} from "../../../utils/formValidation";

// パンくず
const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "Price", path: `/price` },
];

export default function PriceEditPage() {
  const contentKey = "priceContent"; // ページのキー
  const [message, setMessage] = useState(""); // メッセージ
  const [loadingText, setLoadingText] = useState("");

  const [pageContent, setPageContent] = useState(); // ページの表示データ

  // 削除対象のIDを管理
  const [deletedIds, setDeletedIds] = useState([]);

  // messageが文字列の場合、3秒後に自動で消去
  useEffect(() => {
    if (message && message.trim() !== "") {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  // 料金の詳細取得
  const fetchPageContentData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_page_price_content/`, { credentials: "include" });
      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();

      // 状態を更新
      const newPageContent = data.price_content || [];
      setPageContent(newPageContent);
      setDeletedIds([]);

      // iframeにも通知
      setTimeout(() => {
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.TEXT_UPDATE,
              contentKey,
              price_content: newPageContent,
              allData: { price_details: newPageContent },
            },
            process.env.REACT_APP_ORIGIN
          );
        } else {
          console.warn(
            "iframe.contentWindow が存在しません（postMessage 送信失敗）"
          );
        }
      }, 100);
    } catch (error) {
      console.error("データの取得中にエラーが発生しました:", error);
    }
    setLoadingText("");
  }, []);

  useEffect(() => {
    fetchPageContentData(); // 料金詳細の取得
  }, [fetchPageContentData]);

  // 料金詳細を更新
  const handlePageDataSubmit = async () => {
    // 更新するデータ
    const formData = {
      price_content: pageContent,
      deleted_price_ids: deletedIds,
    };

    // バリデーション実行
    const allFormData = {
      price_content: pageContent,
    };

    if (
      !executeValidationWithArrays(
        allFormData,
        validationRules,
        arrayValidationRules,
        fieldLabels,
        setMessage
      )
    ) {
      return;
    }

    setLoadingText("保存中");

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/update_page_price_content/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(`${data.message} status: ${response.status}`);
      }
      setMessage(data.message);
      setDeletedIds([]); // 削除IDをリセット

      // 成功したらデータを再取得して最新の状態に更新
      await fetchPageContentData();

      // iframeにも最新データを通知
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.TEXT_UPDATE,
            contentKey,
            price_content: pageContent,
            allData: { price_content: pageContent },
          },
          process.env.REACT_APP_ORIGIN
        );
      }
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    }
    setLoadingText("");
  };

  // バリデーションルールを定義
  const validationRules = {};

  // 配列要素のバリデーションルール
  const arrayValidationRules = {
    price_content: {
      title: { required: true },
      description: { required: true },
    },
  };

  // フィールドラベルのマッピング
  const fieldLabels = {
    title: "料金 タイトル",
    description: "料金 本文",
    button_text: "ボタン テキスト",
    button_link: "ボタン リンク先",
    button_description: "ボタン 説明",
  };

  // テキスト入力ハンドラー
  const handleInputChange = (e, index) => {
    const { name, value } = e.target;

    // エラークリア処理
    if (
      e.target &&
      e.target.classList &&
      e.target.classList.contains("validation-error")
    ) {
      e.target.classList.remove("validation-error");

      const wrapper = e.target.closest(".form-field-wrapper");
      if (wrapper) {
        const errorLabel = wrapper.querySelector(".validation-error-label");
        if (errorLabel) {
          errorLabel.remove();
        }
      }
    }

    // name属性から実際のフィールド名を抽出
    // price_content0_title -> title
    // button_text -> button_text (そのまま)
    const fieldName = name.startsWith('price_content') 
      ? name.split('_').pop() 
      : name;

    const newPageContent = [...pageContent];
    newPageContent[index][fieldName] = value;
    setPageContent(newPageContent);

    // iframeに通知
    setTimeout(() => {
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        const targetSelector = `.edit_price_${index}_${fieldName}`;

        // データ更新メッセージ
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.TEXT_UPDATE,
            contentKey,
            price_content: newPageContent,
            fieldName: fieldName,
            value: value,
            index: index,
          },
          process.env.REACT_APP_ORIGIN
        );

        // スクロール命令を別途送信
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.SCROLL_TO,
            target: targetSelector,
            contentKey,
          },
          process.env.REACT_APP_ORIGIN
        );
      }
    }, 100);
  };

  // 料金情報の変更ハンドラー
  const handlePriceChange = (itemIndex, priceIndex, field, value) => {
    const newPageContent = [...pageContent];
    if (!newPageContent[itemIndex].prices[priceIndex]) {
      newPageContent[itemIndex].prices[priceIndex] = {};
    }
    newPageContent[itemIndex].prices[priceIndex][field] = value;
    setPageContent(newPageContent);

    // iframeに通知
    setTimeout(() => {
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        const targetSelector = `.edit_price_${itemIndex}_${priceIndex}_${field}`;

        // データ更新メッセージ
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.TEXT_UPDATE,
            contentKey,
            price_content: newPageContent,
            allData: { price_details: newPageContent },
          },
          process.env.REACT_APP_ORIGIN
        );

        // スクロール命令を別途送信
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.SCROLL_TO,
            target: targetSelector,
            contentKey,
          },
          process.env.REACT_APP_ORIGIN
        );
      }
    }, 100);
  };

  // 料金セクションの追加
  const handleAddPriceSection = (index) => {
    const newPageContent = [...pageContent];
    const newSection = {
      title: "",
      prices: [],
      button_link: "",
      button_text: "",
      description: "",
      button_description: "",
    };
    newPageContent.splice(index + 1, 0, newSection);
    setPageContent(newPageContent);

    // 新しいユーティリティ関数を使用
    handlePriceSectionAdd(newSection, index + 1, {
      contentKey,
      currentData: newPageContent,
    });
  };

  // 料金セクションの削除
  const handleDeletePriceSection = (index, section) => {
    // 削除対象のIDを追加（APIで使用）
    if (section.id) {
      setDeletedIds([...deletedIds, section.id]);
    }

    // 配列から削除
    const newPageContent = [...pageContent];
    newPageContent.splice(index, 1);

    // 最後の1つを削除しようとしている場合は、空のフォームをセット
    if (newPageContent.length === 0) {
      newPageContent.push({
        title: "",
        prices: [{ label: "", amount: "" }],
        button_link: "",
        button_text: "",
        description: "",
        button_description: "",
      });
    }

    setPageContent(newPageContent);

    // iframe内のコンテンツを更新
    setTimeout(() => {
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        // まず全体のデータを更新するメッセージを送信
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.TEXT_UPDATE,
            contentKey,
            price_content: newPageContent,
            allData: { price_details: newPageContent },
          },
          process.env.REACT_APP_ORIGIN
        );

        // 削除メッセージも送信（3001側で特別な処理が必要な場合）
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.DELETE_PRICE_SECTION,
            contentKey,
            sectionId: section.id,
            priceContent: newPageContent,
          },
          process.env.REACT_APP_ORIGIN
        );
      }
    }, 100);
  };

  // 料金項目を追加
  const handleAddPrice = (itemIndex, priceIndex) => {
    const newPageContent = [...pageContent];
    const newPriceItem = { label: "", amount: "" };
    newPageContent[itemIndex].prices.splice(priceIndex + 1, 0, newPriceItem);
    setPageContent(newPageContent);

    // 新しいユーティリティ関数を使用
    handlePriceItemAdd(newPriceItem, itemIndex, priceIndex + 1, {
      contentKey,
      currentData: newPageContent,
    });
  };

  // 料金項目を削除
  const handleRemovePrice = (itemIndex, priceIndex) => {
    const newPageContent = [...pageContent];
    newPageContent[itemIndex].prices.splice(priceIndex, 1);

    // 最後の料金項目を削除した場合は、空の料金項目を1つ追加
    // if (newPageContent[itemIndex].prices.length === 0) {
    //   newPageContent[itemIndex].prices.push({ label: "", amount: "" });
    // }

    setPageContent(newPageContent);

    setTimeout(() => {
      // iframeに通知 - 全体のデータを送信して確実に更新
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        // 全体のデータを更新するメッセージを送信
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.TEXT_UPDATE,
            contentKey,
            price_content: newPageContent,
            allData: { price_content: newPageContent },
          },
          process.env.REACT_APP_ORIGIN
        );

        // 削除メッセージも送信（3001側で特別な処理が必要な場合）
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.DELETE_PRICE_ITEM,
            contentKey,
            itemIndex: itemIndex,
            priceIndex: priceIndex,
            priceContent: newPageContent,
          },
          process.env.REACT_APP_ORIGIN
        );
      }
    }, 100);
  };

  // スタイル要素を作成
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = `
      .forms {
        display: grid !important;
        grid-template-columns: auto !important;
      }
    `;

    // head に追加
    document.head.appendChild(styleElement);

    // クリーンアップ関数
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            {/* パンくず */}
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />

            <div className={`message ${message ? "success" : ""}`}>
              <p>{message}</p>
            </div>

            {/* 編集フォーム */}
            <div className="form">
              {pageContent && pageContent.length === 0 ? (
                <section>
                  <div className="area4">
                    <div className="item">
                      <div className="ctrl">
                        <p>0</p>
                        <div className="icon-wrap">
                          <i
                            className="icon delete"
                            style={{ opacity: 0.3, cursor: "not-allowed" }}
                          ></i>
                          <i
                            className="icon add"
                            onClick={() => handleAddPriceSection(-1)}
                            style={{ cursor: "pointer" }}
                          ></i>
                        </div>
                      </div>
                      <p>
                        料金セクションがありません。「+」ボタンで新しいセクションを追加してください。
                      </p>
                    </div>
                  </div>
                </section>
              ) : (
                pageContent &&
                pageContent.map((item, index) => (
                  <section key={index}>
                    <div className="area2">
                      <div className="item">
                        {/* セクションコントロール */}
                        <div className="ctrl">
                          <p>{index + 1}</p>
                          <div className="icon-wrap">
                            <i
                              className="icon delete"
                              onClick={() =>
                                handleDeletePriceSection(index, item)
                              }
                              style={{
                                cursor:
                                  pageContent.length <= 1
                                    ? "not-allowed"
                                    : "pointer",
                                opacity: pageContent.length <= 1 ? 0.5 : 1,
                              }}
                            ></i>
                            <i
                              className="icon add"
                              onClick={() => handleAddPriceSection(index)}
                              style={{ cursor: "pointer" }}
                            ></i>
                          </div>
                        </div>
                        <div
                          className="forms"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* 料金 タイトル */}
                          <input
                            type="text"
                            name={`price_content${index}_title`}
                            value={item.title || ""}
                            placeholder="タイトルを入力"
                            onChange={(e) => handleInputChange(e, index)}
                            onFocus={(e) => handleInputChange(e, index)}
                            data-label="料金 タイトル"
                            data-required="true"
                            data-index={index}
                          />

                          {/* 料金 本文 */}
                          <textarea
                            name={`price_content${index}_description`}
                            value={item.description || ""}
                            placeholder="内容を入力"
                            onChange={(e) => handleInputChange(e, index)}
                            onFocus={(e) => handleInputChange(e, index)}
                            data-label="料金 本文"
                            data-required="true"
                            data-index={index}
                            rows={5}
                          />

                          {/* 料金（動的） */}
                          <dl className="prices txt-form">
                            {(item.prices || []).map((price, i) => (
                              <React.Fragment key={i}>
                                <dt>
                                  <input
                                    type="text"
                                    name="label"
                                    value={price.label || ""}
                                    placeholder="料金区分を入力"
                                    onChange={(e) =>
                                      handlePriceChange(
                                        index,
                                        i,
                                        "label",
                                        e.target.value
                                      )
                                    }
                                    onFocus={(e) =>
                                      handlePriceChange(
                                        index,
                                        i,
                                        "label",
                                        e.target.value
                                      )
                                    }
                                    data-label={`料金区分 ${i + 1}`}
                                    data-index={`${index}-${i}`}
                                  />
                                </dt>
                                <dd
                                  onClick={(e) => {
                                    // input要素のクリックでは削除しない
                                    if (e.target.tagName !== "INPUT") {
                                      handleRemovePrice(index, i);
                                    }
                                  }}
                                  style={{ cursor: "pointer" }}
                                >
                                  <input
                                    type="text"
                                    name="amount"
                                    value={price.amount || ""}
                                    placeholder="金額を入力"
                                    onChange={(e) =>
                                      handlePriceChange(
                                        index,
                                        i,
                                        "amount",
                                        e.target.value
                                      )
                                    }
                                    onFocus={(e) =>
                                      handlePriceChange(
                                        index,
                                        i,
                                        "amount",
                                        e.target.value
                                      )
                                    }
                                    data-label={`金額 ${i + 1}`}
                                    data-index={`${index}-${i}`}
                                  />
                                </dd>
                              </React.Fragment>
                            ))}
                          </dl>

                          <div className="accent-wrap">
                            <p
                              onClick={() =>
                                handleAddPrice(
                                  index,
                                  (item.prices || []).length - 1
                                )
                              }
                              style={{ cursor: "pointer" }}
                            >
                              <i className="icon add"></i>
                              <span>Price</span>
                            </p>
                          </div>

                          {/* ボタン テキスト */}
                          <input
                            type="text"
                            name="button_text"
                            value={item.button_text || ""}
                            placeholder="テキストを入力"
                            onChange={(e) => handleInputChange(e, index)}
                            onFocus={(e) => handleInputChange(e, index)}
                            data-label="ボタン テキスト"
                            data-index={index}
                          />

                          {/* ボタン リンク先 */}
                          <input
                            type="text"
                            name="button_link"
                            value={item.button_link || ""}
                            placeholder="リンク先を入力"
                            onChange={(e) => handleInputChange(e, index)}
                            onFocus={(e) => handleInputChange(e, index)}
                            data-label="ボタン リンク先"
                            data-index={index}
                          />

                          {/* ボタン 説明 */}
                          <input
                            type="text"
                            name="button_description"
                            value={item.button_description || ""}
                            placeholder="ボタン下の説明を入力"
                            onChange={(e) => handleInputChange(e, index)}
                            onFocus={(e) => handleInputChange(e, index)}
                            data-label="ボタン 説明"
                            data-index={index}
                          />
                        </div>
                      </div>
                    </div>
                  </section>
                ))
              )}
            </div>
            {/* 送信ボタン */}
            <div className="submit-area">
              <button type="reset" onClick={() => handleResetWithValidation(fetchPageContentData)}>
                Reset
              </button>
              <button type="submit" onClick={handlePageDataSubmit}>
                Submit
              </button>
            </div>
          </>
        )}
      </ResizableEditArea>

      {/* ローディング */}
      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div> <p>{loadingText}...</p>
          </div>
        </div>
      )}

      {/* プレビュー */}
      <Iframe path={"/price"} data={pageContent} contentKey={contentKey} />
    </>
  );
}
